/**
 * Suite of functions involved in manipulating the typedValue of type STORABLE_PLACEHOLDERS.
 */
import {assert} from 'Asserts';
import {StorableInfo, StorablePlaceholdersTypedValue} from 'notebook/tables/typed-values/TypedValueTypes';

export type FormattedStorableGroup =
  | {
      type: 'NEW_STORABLES';
      count: number;
    }
  | {
      type: 'EXISTING_STORABLE';
      storableSummary: StorableInfo;
    };

/**
 * Produce an intermediate representation of the cell that can be subsequently rendered in the table
 * or converted into a string for pasting.
 *
 * Keep in sync with format_storable_placeholders_typed_value in
 * benchling/notebook/structured_tables/export/storable_placeholders_typed_value.py
 */
export function formatStorablePlaceholdersTypedValue(
  typedValue: StorablePlaceholdersTypedValue
): Array<FormattedStorableGroup> {
  const {storablePlaceholders} = typedValue;
  const formattedStorableGroups: Array<FormattedStorableGroup> = [];
  for (const storablePlaceholder of storablePlaceholders) {
    if (storablePlaceholder != null) {
      formattedStorableGroups.push({
        type: 'EXISTING_STORABLE',
        storableSummary: storablePlaceholder,
      });
      continue;
    }

    const lastGroup = formattedStorableGroups[formattedStorableGroups.length - 1];
    if (formattedStorableGroups.length === 0 || lastGroup.type === 'EXISTING_STORABLE') {
      formattedStorableGroups.push({
        type: 'NEW_STORABLES',
        count: 1,
      });
    } else {
      assert(lastGroup.type === 'NEW_STORABLES', `Unexpected FormattedStorableGroup type ${lastGroup.type}`);
      lastGroup.count++;
    }
  }
  return formattedStorableGroups;
}

function convertFormattedPartToString(formattedPart: FormattedStorableGroup): string {
  if (formattedPart.type === 'NEW_STORABLES') {
    return `${formattedPart.count} new`;
  } else {
    return formattedPart.storableSummary.name;
  }
}

/**
 * Convert the cell into a string representation, e.g., for pasting.
 */
export function convertStorablePlaceholdersTypedValueToString(
  typedValue: StorablePlaceholdersTypedValue
): string {
  const intermediateFormat = formatStorablePlaceholdersTypedValue(typedValue);
  const stringParts = intermediateFormat.map((formattedPart) => convertFormattedPartToString(formattedPart));
  return stringParts.join(', ');
}
